import { Button, Modal, Select, Tooltip } from 'antd';
import { useAppSelector } from 'store/hook';
import classes from './PaymentFlowSelection.module.scss';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { useState, useEffect } from 'react';
import api from 'api';
import billing from 'api/billing';
import { TFunction } from 'i18next';
import { QuoteData } from 'components/TariffsComponent/tariffsData';
import { getTheme } from 'helpers/getTheme';

type GroupedTariff = {
  tariffId: number;
  tariffName: string;
  quoteName: string;
};

type PaymentFlowSelectionProps = {
  open: boolean
  onCancel: () => void;
};

const getTariffLabel = (tariff: GroupedTariff, t: TFunction) => {
  return (
    <>
      {tariff.quoteName && (
        <>
          <Tooltip title={tariff.quoteName} placement="topLeft">
            <span className={classes.quoteName}>[{tariff.quoteName}]</span>
          </Tooltip>
          :{' '}
        </>
      )}
      {t(`Tariffs.tariff_name.${tariff.tariffName}`)}
    </>
  );
};

export default function PaymentFlowSelection({open, onCancel}: PaymentFlowSelectionProps) {
      
  const { available_rooms } = useAppSelector(store => store.userData);
  const { messageApi } = useAppSelector(store => store.windowState);
  const { t } = useTranslation();
  
  const ended_tariff_lifetime = localStorage.getItem('ended_tariff_lifetime');

  const targetRoom = available_rooms.find(room => room.id === ended_tariff_lifetime);
  const nameOfExpiredRoom = targetRoom?.name;
  const [quotes, setQuotes] = useState<QuoteData[]>([]);
  const [groupedTariffs, setGroupedTariffs] = useState<GroupedTariff[]>([]);
  const [selectedQuoteName, setSelectedQuoteName] = useState<string | undefined>(undefined);
  const [isLoadingTariffs, setIsLoadingTariffs] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);

  useEffect(() => {
    const loadTariffsAndQuotes = async () => {
      if (!open) return;
      setIsLoadingTariffs(true);
      try {
        const { data } = await api.getTariffDetails();

        const grouped: GroupedTariff[] = data.map((tariff: any) => ({
          tariffId: tariff.id,
          tariffName: tariff.name,
          quoteName: tariff.quote_name,
        }));

        setGroupedTariffs(grouped);

        if (data.length > 0) {
          const { data: quotesData } = await api.getQuotes();
          setQuotes(quotesData);
        }
      } catch {
        setGroupedTariffs([]);
        setQuotes([]);
      } finally {
        setIsLoadingTariffs(false);
      }
    };
    loadTariffsAndQuotes();
  }, [open, t]);

  const handleTransfer = async () => {
    if (!selectedQuoteName || !targetRoom?.id) {
      messageApi?.warning(t('Tariffs.flow_selection.select_tariff'));
      return;
    }
    setIsTransferring(true);
    try {
      const selectedQuote = quotes.find((quota) => quota.name === selectedQuoteName);
      if (!selectedQuote) {
        messageApi?.error(t('Tariffs.flow_selection.transfer_error'));
        return;
      }
      await billing.changeQuote({ room_id: targetRoom.id, quote_id: selectedQuote.id });
      messageApi?.success(t('Tariffs.flow_selection.transfer_success'));
      localStorage.removeItem('ended_tariff_lifetime');
      onCancel();
    } catch {
      messageApi?.error(t('Tariffs.flow_selection.transfer_error'));
    } finally {
      setIsTransferring(false);
    }
  };

  return (
    <Modal 
      styles={{ body: {maxHeight: '85vh'} }}
      width={'auto'}
      closeIcon={true}
      keyboard={false}
      mask={{ closable: false }}
      title={t(`Tariffs.tariffSelect`)}
      open={open}
      centered={true}
      onCancel={()=>{
        localStorage.removeItem('ended_tariff_lifetime')
        onCancel()
      }}
      footer={null}
      className={cn(classes.modalAddRoom, getTheme())}
    >
      <div className={classes.modalWrap}>
        <div className={classes.text}>{t(`Tariffs.flow_selection.want_to_change`, { nameOfExpiredRoom })}</div>
        <div className={classes.itemsWrap}>
          <div className={classes.item}>
            <div className={cn(classes.text, classes.textItem)}>{t(`Tariffs.flow_selection.transfer_to_paid`)}</div>
            <Select
              placeholder={t('Tariffs.flow_selection.select_tariff')}
              value={selectedQuoteName}
              onChange={(value) => setSelectedQuoteName(value)}
              style={{ width: '100%', marginBottom: '12px' }}
              loading={isLoadingTariffs}
              options={
                groupedTariffs.map((tariff) => ({
                  label: getTariffLabel(tariff, t),
                  value: tariff.quoteName,
                }))
              }
            />
            <Button
              type='primary'
              onClick={handleTransfer}
              loading={isTransferring}
              disabled={!selectedQuoteName || isLoadingTariffs}
            >
              {t(`Tariffs.flow_selection.transfer_button`)}
            </Button>
          </div>
          <div className={classes.item}>
            <div className={cn(classes.text, classes.textItem)}>{t(`Tariffs.flow_selection.or_buy_new_tariff`)}</div>
            <Button
              type='primary'
              onClick={onCancel}
            >
              {t(`Tariffs.flow_selection.transfer_button`)}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
